/**
 * v0.3 データ公開ゲート フロントエンド型定義
 * バックエンド (apps/api/src/routes/publish.ts) のレスポンス形と対応
 */
// v0.4 章⑤ (D-15/D-23/D-79/D-126): preliminary (速報公開) を追加。
// ⚠️ isVisibleStatus は API 側 (lib/publish-gate/constants.ts) と同じ定義を1本だけ持つ (§8-93 対策)
export type PublishStatus = 'draft' | 'preliminary' | 'published';
export const isVisibleStatus = (status: PublishStatus | string): boolean => status === 'published' || status === 'preliminary';
export type AiCommentStatus = 'pending' | 'generating' | 'succeeded' | 'failed';
export type NotApplicableScope = 'permanent' | 'spot';

export type PublishGateInfo = {
  excluded_store_ids: string[];
  excluded_store_names: { node_id: string; node_name: string }[];
  partial_month: boolean;
  viewing_draft: boolean;
  preliminary_store_ids: string[];
  viewing_preliminary: boolean;
  /** §8-151 (Lite 版): 未公開のため集計から外した月。Lite は「1ノード × 複数月」で
   *  可視性が決まるため、除外の単位が店舗ではなく月になる (Full では未使用)。 */
  excluded_months?: string[];
  /** 店舗比較 (/trends-comparison) 専用: 複数月の範囲を集約しているため、
   *  「n/12ヶ月が未公開」の形で件数を示せるよう月数を返す (単月ビューでは未使用)。 */
  excluded_month_count?: number;
  total_month_count?: number;
};

export type InputReadySummary = {
  total_data_types: number;
  ready_count: number;
  not_applicable_count: number;
};

export type PublishStatusCell = {
  target_month: string;
  status: PublishStatus;
  partial_publish: boolean;
  input_ready_summary: InputReadySummary;
  published_at: string | null;
  published_by_name: string | null;
  has_ai_comment: boolean;
  ai_comment_status: AiCommentStatus | null;
};

export type PublishStatusNode = {
  node_id: string;
  node_name: string;
  node_type_code: string;
  cells: PublishStatusCell[];
};

export type PublishStatusResponse = {
  months: string[];
  nodes: PublishStatusNode[];
};

export type DataTypeInputStatus = {
  inputStatusId: string;
  tenantId: string;
  nodeId: string;
  targetMonth: string;
  dataType: string;
  inputReady: number;
  markedReadyAt: string | null;
  markedReadyByUserId: string | null;
  autoRevertedAt: string | null;
  autoRevertReason: string | null;
};

export type NotApplicableDeclaration = {
  declarationId: string;
  nodeId: string;
  dataType: string;
  scope: NotApplicableScope;
  targetMonth: string | null;
  declaredAt: string;
  note: string | null;
};

export type AiPeriodComment = {
  commentId: string;
  dataSnapshotHash: string;
  commentBody: string;
  status: AiCommentStatus;
  errorMessage: string | null;
  generatedAt: string | null;
  /** v0.3.x AIコメント カスタム指示: 生成に使われた指示 (表示用)。
   *  Full版は公開時点の設定で1回だけ生成されるため、後から設定を変えてもここは変わらない。 */
  used_instructions?: Array<{ instruction_id: string; title: string }>;
  /** v0.4 章⑤ (D-21): 速報→確定の再生成が失敗した場合に埋まる。非nullなら「このコメントは
   *  速報値に基づいています」等の stale 表示を出す */
  regeneration_failed_at?: string | null;
  stale_reason?: string | null;
};

export type PublishDetailResponse = {
  publish_status: {
    publishStatusId: string;
    status: PublishStatus;
    publishedAt: string | null;
    partialPublish: number;
    missingDataTypes: string | null;
  } | null;
  input_status: DataTypeInputStatus[];
  not_applicable: NotApplicableDeclaration[];
  ai_comment: AiPeriodComment | null;
  history: {
    effective_from: string;
    effective_to: string | null;
    status: PublishStatus;
    published_by_name: string | null;
  }[];
};

export const DATA_TYPE_LABELS: Record<string, string> = {
  sales: '売上',
  delivery_sales: 'デリバリー売上',
  delivery_details: '納品明細',
  labor: '人件費',
  opex: '諸経費',
};
